import moment from 'moment';
import type { DashboardSmallUser } from 'api/models/DashboardSmallUser';
import { WorkboardTask } from 'api/models/WorkboardTask';
import { CALENDAR_DATE_FORMAT } from 'dashboard409a/common/constants/calendar';
import type { SelectedUserWithGroup } from 'dashboard409a/pages/Workboard/UserGroups/UserGroups.types';
import type { RecurrenceEndMode } from './CreateWorkboardTaskModal.constants';

export type CreateWorkboardTaskFormState = {
  name: string;
  startsOn: Date | null;
  occurrenceDate: Date | null;
  status: WorkboardTask.status;
  assignedTo: SelectedUserWithGroup[];
  assignedTaskManager: SelectedUserWithGroup[];
  linkToRelatedDocuments: string;
  fileReferenceFolder: string;
  isWaiting: boolean;
  isRush: boolean;
  recurrenceFrequency: WorkboardTask.recurrence_frequency;
  recurrenceInterval: number;
  recurrenceByWeekday: string[];
  recurrenceBySetpos: number | null;
  recurrenceByMonthday: number | null;
  recurrenceEndMode: RecurrenceEndMode;
  recurrenceEndsOn: Date | null;
  recurrenceCount: number | null;
};

const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

export const getCustomUserDisplayName = (user: DashboardSmallUser | null): string => {
  if (!user) {
    return 'Unassigned';
  }

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return fullName || user.email || 'Unknown User';
};

export const getCustomUsersDisplayName = (users: SelectedUserWithGroup[]): string => {
  if (users.length === 0) {
    return 'Unassigned';
  }

  if (users.length === 1) {
    return getCustomUserDisplayName(users[0]);
  }

  return `${getCustomUserDisplayName(users[0])}, +${users.length - 1} more`;
};

export const getCustomUserAvatarData = (user: DashboardSmallUser | null) =>
  user?.picture_url ? [{ picture_url: user.picture_url }] : [];

export const getCustomUsersAvatarData = (users: SelectedUserWithGroup[]) =>
  users.flatMap(user => getCustomUserAvatarData(user));

const BUSINESS_WEEKDAY_CODES = new Set(['MO', 'TU', 'WE', 'TH', 'FR']);

export const getWeekdayCodeFromDate = (date: Date) => {
  const weekdayCode = WEEKDAY_CODES[moment(date).day()];
  // Recurring series never stay on a weekend; Saturday and Sunday start on Monday.
  if (!BUSINESS_WEEKDAY_CODES.has(weekdayCode)) {
    return 'MO';
  }
  return weekdayCode;
};

export const isRecurringFrequency = (
  frequency?: WorkboardTask.recurrence_frequency | null
): boolean =>
  (frequency ?? WorkboardTask.recurrence_frequency.NONE) !== WorkboardTask.recurrence_frequency.NONE;

export const getDefaultFormState = (defaultStartsOn?: Date | null): CreateWorkboardTaskFormState => ({
  name: '',
  startsOn: defaultStartsOn ?? new Date(),
  occurrenceDate: defaultStartsOn ?? new Date(),
  status: WorkboardTask.status.PENDING,
  assignedTo: [],
  assignedTaskManager: [],
  linkToRelatedDocuments: '',
  fileReferenceFolder: '',
  isWaiting: false,
  isRush: false,
  recurrenceFrequency: WorkboardTask.recurrence_frequency.NONE,
  recurrenceInterval: 1,
  recurrenceByWeekday: [],
  recurrenceBySetpos: null,
  recurrenceByMonthday: null,
  recurrenceEndMode: 'none',
  recurrenceEndsOn: null,
  recurrenceCount: null,
});

export const validateCreateWorkboardTaskForm = (form: CreateWorkboardTaskFormState): string | null => {
  if (!form.name.trim()) {
    return 'Name is required.';
  }

  if (!form.occurrenceDate) {
    return 'Due date is required.';
  }

  if (form.recurrenceInterval < 1) {
    return 'Recurrence interval must be at least 1.';
  }

  const isRecurring = isRecurringFrequency(form.recurrenceFrequency);

  if (isRecurring) {
    if (form.recurrenceEndMode === 'date') {
      if (!form.recurrenceEndsOn) {
        return 'Recurrence end date is required.';
      }
      if (moment(form.recurrenceEndsOn).isBefore(moment(form.occurrenceDate), 'day')) {
        return 'Recurrence end date must be on or after the due date.';
      }
    }

    if (form.recurrenceEndMode === 'count' && (!form.recurrenceCount || form.recurrenceCount < 1)) {
      return 'Recurrence count must be at least 1.';
    }

    if (
      form.recurrenceFrequency === WorkboardTask.recurrence_frequency.WEEKLY &&
      form.recurrenceByWeekday.length === 0
    ) {
      return 'Select at least one weekday for weekly recurrence.';
    }

    if (
      form.recurrenceFrequency === WorkboardTask.recurrence_frequency.MONTHLY &&
      form.recurrenceBySetpos !== null &&
      form.recurrenceByWeekday.length === 0
    ) {
      return 'Select at least one weekday when using an ordinal monthly pattern.';
    }
  }

  return null;
};

const parseWeekdays = (rawWeekdays: WorkboardTask['recurrence_by_weekday']): string[] => {
  if (!Array.isArray(rawWeekdays)) {
    return [];
  }

  const weekdayCodes = rawWeekdays
    .filter((value): value is string => typeof value === 'string')
    .map(value => value.toUpperCase());
  const businessDays = weekdayCodes.filter(value => BUSINESS_WEEKDAY_CODES.has(value));
  if (businessDays.length === 0 && weekdayCodes.length > 0) {
    return ['MO'];
  }
  return [...new Set(businessDays)];
};

const getRecurrenceEndMode = (task: WorkboardTask): RecurrenceEndMode => {
  if (task.recurrence_ends_on) {
    return 'date';
  }

  if (task.recurrence_count) {
    return 'count';
  }

  return 'none';
};

export const mapWorkboardTaskToFormState = (
  task: WorkboardTask,
  staffUsers: DashboardSmallUser[],
  occurrenceDateProp?: string | null,
  occurrenceStatus?: WorkboardTask.status | null,
  rescheduledTo?: string | null,
  occurrenceIsRush?: boolean | null
): CreateWorkboardTaskFormState => {
  const findUsers = (userIds?: number[] | null): SelectedUserWithGroup[] =>
    (userIds ?? [])
      .map(userId => staffUsers.find(user => user.id === userId) ?? null)
      .filter((user): user is DashboardSmallUser => user != null)
      .map(user => ({ ...user }));

  const startsOn = moment(task.starts_on, CALENDAR_DATE_FORMAT, true);
  const recurrenceEndMode = getRecurrenceEndMode(task);
  const isRecurring = isRecurringFrequency(task.recurrence_frequency);
  const displayDateSource = rescheduledTo || occurrenceDateProp;
  const parsedOccurrenceDate = displayDateSource ? moment(displayDateSource, CALENDAR_DATE_FORMAT, true) : null;
  const startsOnDate = startsOn.isValid() ? startsOn.toDate() : new Date();
  const occurrenceDate = parsedOccurrenceDate?.isValid() ? parsedOccurrenceDate.toDate() : startsOnDate;

  return {
    name: task.name,
    startsOn: startsOnDate,
    occurrenceDate,
    status: occurrenceStatus ?? task.status ?? WorkboardTask.status.PENDING,
    assignedTo: findUsers(task.assignees),
    assignedTaskManager: findUsers(task.task_managers),
    linkToRelatedDocuments: task.link_to_related_documents ?? '',
    fileReferenceFolder: task.file_reference_folder ?? '',
    isWaiting: task.is_waiting ?? false,
    isRush: isRecurring ? (occurrenceIsRush ?? false) : (task.is_rush ?? false),
    recurrenceFrequency: task.recurrence_frequency ?? WorkboardTask.recurrence_frequency.NONE,
    recurrenceInterval: task.recurrence_interval ?? 1,
    recurrenceByWeekday: parseWeekdays(task.recurrence_by_weekday),
    recurrenceBySetpos: task.recurrence_by_setpos ?? null,
    recurrenceByMonthday: task.recurrence_by_monthday ?? null,
    recurrenceEndMode,
    recurrenceEndsOn:
      task.recurrence_ends_on && moment(task.recurrence_ends_on, CALENDAR_DATE_FORMAT, true).isValid()
        ? moment(task.recurrence_ends_on, CALENDAR_DATE_FORMAT).toDate()
        : null,
    recurrenceCount: task.recurrence_count ?? null,
  };
};

type BuildWorkboardTaskPayloadOptions = {
  forUpdate?: boolean;
};

export type WorkboardTaskPayload = WorkboardTask;

export type WorkboardTaskOccurrencePatchPayload = {
  occurrence_date: string;
  status: WorkboardTask.status;
  rescheduled_to: string | null;
  is_rush: boolean;
};

export const buildWorkboardTaskOccurrencePatchPayload = (
  form: CreateWorkboardTaskFormState,
  scheduledOccurrenceDate: string
): WorkboardTaskOccurrencePatchPayload => {
  const dueDate = moment(form.occurrenceDate!).format(CALENDAR_DATE_FORMAT);
  return {
    // Keep the series instance identity; move via rescheduled_to.
    occurrence_date: scheduledOccurrenceDate,
    status: form.status,
    rescheduled_to: dueDate === scheduledOccurrenceDate ? null : dueDate,
    is_rush: form.isRush,
  };
};

const mapUserIds = (users: SelectedUserWithGroup[]): number[] =>
  users.map(user => user.id).filter((id): id is number => id != null);

const buildAssigneesAndReferences = (
  form: CreateWorkboardTaskFormState,
  link: string,
  folder: string,
  forUpdate: boolean
): Partial<WorkboardTaskPayload> => {
  if (forUpdate) {
    return {
      assignees: mapUserIds(form.assignedTo),
      task_managers: mapUserIds(form.assignedTaskManager),
      link_to_related_documents: link || null,
      file_reference_folder: folder || null,
    };
  }

  return {
    ...(form.assignedTo.length > 0 ? { assignees: mapUserIds(form.assignedTo) } : {}),
    ...(form.assignedTaskManager.length > 0 ? { task_managers: mapUserIds(form.assignedTaskManager) } : {}),
    ...(link ? { link_to_related_documents: link } : {}),
    ...(folder ? { file_reference_folder: folder } : {}),
  };
};

const CLEARED_RECURRENCE_FIELDS: Partial<WorkboardTaskPayload> = {
  recurrence_interval: 1,
  recurrence_by_weekday: null,
  recurrence_by_setpos: null,
  recurrence_by_monthday: null,
  recurrence_ends_on: null,
  recurrence_count: null,
};

const shouldIncludeWeekdays = (form: CreateWorkboardTaskFormState): boolean =>
  form.recurrenceFrequency === WorkboardTask.recurrence_frequency.WEEKLY ||
  (form.recurrenceFrequency === WorkboardTask.recurrence_frequency.MONTHLY &&
    form.recurrenceBySetpos !== null &&
    form.recurrenceByMonthday === null);

const buildRecurrenceWeekdays = (
  form: CreateWorkboardTaskFormState,
  forUpdate: boolean
): Partial<WorkboardTaskPayload> => {
  if (shouldIncludeWeekdays(form) && form.recurrenceByWeekday.length > 0) {
    return { recurrence_by_weekday: form.recurrenceByWeekday };
  }

  return forUpdate ? { recurrence_by_weekday: null } : {};
};

const buildMonthlyRecurrencePattern = (
  form: CreateWorkboardTaskFormState,
  forUpdate: boolean
): Partial<WorkboardTaskPayload> => {
  const isMonthly = form.recurrenceFrequency === WorkboardTask.recurrence_frequency.MONTHLY;

  if (isMonthly && form.recurrenceByMonthday !== null) {
    return {
      recurrence_by_monthday: form.recurrenceByMonthday,
      ...(forUpdate ? { recurrence_by_setpos: null } : {}),
    };
  }

  if (isMonthly && form.recurrenceBySetpos !== null) {
    return {
      recurrence_by_setpos: form.recurrenceBySetpos,
      ...(forUpdate ? { recurrence_by_monthday: null } : {}),
    };
  }

  return forUpdate ? { recurrence_by_setpos: null, recurrence_by_monthday: null } : {};
};

const buildRecurrenceEnd = (
  form: CreateWorkboardTaskFormState,
  forUpdate: boolean
): Partial<WorkboardTaskPayload> => {
  if (form.recurrenceEndMode === 'date' && form.recurrenceEndsOn) {
    return {
      recurrence_ends_on: moment(form.recurrenceEndsOn).format(CALENDAR_DATE_FORMAT),
      ...(forUpdate ? { recurrence_count: null } : {}),
    };
  }

  if (form.recurrenceEndMode === 'count' && form.recurrenceCount) {
    return {
      recurrence_count: form.recurrenceCount,
      ...(forUpdate ? { recurrence_ends_on: null } : {}),
    };
  }

  return forUpdate ? { recurrence_ends_on: null, recurrence_count: null } : {};
};

const buildRecurrenceFields = (
  form: CreateWorkboardTaskFormState,
  forUpdate: boolean
): Partial<WorkboardTaskPayload> => {
  if (form.recurrenceFrequency === WorkboardTask.recurrence_frequency.NONE) {
    return forUpdate ? CLEARED_RECURRENCE_FIELDS : {};
  }

  return {
    recurrence_interval: form.recurrenceInterval,
    ...buildRecurrenceWeekdays(form, forUpdate),
    ...buildMonthlyRecurrencePattern(form, forUpdate),
    ...buildRecurrenceEnd(form, forUpdate),
  };
};

export const buildWorkboardTaskPayload = (
  form: CreateWorkboardTaskFormState,
  options?: BuildWorkboardTaskPayloadOptions
): WorkboardTaskPayload => {
  const forUpdate = options?.forUpdate ?? false;
  const link = form.linkToRelatedDocuments.trim();
  const folder = form.fileReferenceFolder.trim();
  const isRecurring = isRecurringFrequency(form.recurrenceFrequency);
  const isOccurrenceEdit = forUpdate && isRecurring && form.occurrenceDate != null;
  const startsOnSource = isRecurring && forUpdate ? form.startsOn : (form.occurrenceDate ?? form.startsOn);

  return {
    name: form.name.trim(),
    starts_on: moment(startsOnSource).format(CALENDAR_DATE_FORMAT),
    is_waiting: form.isWaiting,
    recurrence_frequency: form.recurrenceFrequency,
    ...(!isOccurrenceEdit ? { status: form.status } : {}),
    ...(!isRecurring ? { is_rush: form.isRush } : {}),
    ...buildAssigneesAndReferences(form, link, folder, forUpdate),
    ...buildRecurrenceFields(form, forUpdate),
  };
};
